import { CheckCircle2, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ChipInput } from "@/components/app/ChipInput";
import { InfoTip } from "@/components/app/Common";
import { openProtectedFile } from "@/lib/api";
import { QualityPanel } from "@/pages/wizard/QualityPanel";
import { TraitsEditor, KnockoutEditor, ListEditor } from "@/pages/wizard/Editors";
import { LegalFields } from "@/pages/job/LegalFields";

const Section = ({ title, tip, children, testId }) => (
  <section className="panel space-y-3 p-5" data-testid={testId}>
    <h3 className="flex items-center gap-1.5 text-base font-semibold">{title}{tip && <InfoTip text={tip} />}</h3>
    {children}
  </section>
);

function OriginalText({ job, setJob }) {
  return (
    <div className="space-y-4 lg:sticky lg:top-24 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto">
      <QualityPanel quality={job.jd_quality} jdText={job.jd_text || ""} onApply={(t) => setJob((j) => ({ ...j, jd_text: t }))} />
      <section className="panel p-5" data-testid="jd-original-text">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold">Original job description</h3>
          {job.jd_file_url && (
            <button className="inline-flex items-center gap-1 text-sm text-brand hover:underline" onClick={() => openProtectedFile(job.jd_file_url)} data-testid="review-view-file-btn">
              <FileText size={14} /> View file
            </button>
          )}
        </div>
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-secondary">{job.jd_text}</p>
      </section>
    </div>
  );
}

export function ReviewEditor({ job, setJob, onConfirm, confirming }) {
  const req = job.requirements || {};
  const setReq = (k, v) => setJob((j) => ({ ...j, requirements: { ...j.requirements, [k]: v } }));
  const set = (k, v) => setJob((j) => ({ ...j, [k]: v }));

  return (
    <div className="space-y-6" data-testid="wizard-step-review">
      <div className="grid gap-6 lg:grid-cols-2">
        <OriginalText job={job} setJob={setJob} />
        <div className="space-y-4" data-testid="requirements-editor">
          <Section title="Basics" testId="review-basics">
            <div className="space-y-2"><Label>Job title</Label><Input value={job.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="review-title-input" /></div>
            <div className="space-y-2"><Label>Summary</Label><Textarea value={job.summary || ""} onChange={(e) => set("summary", e.target.value)} className="min-h-[80px] text-sm" data-testid="review-summary-input" /></div>
            <div className="grid grid-cols-2 gap-3 max-sm:grid-cols-1">
              <div className="space-y-2"><Label>Seniority</Label><Input value={job.seniority || ""} onChange={(e) => set("seniority", e.target.value)} data-testid="review-seniority-input" /></div>
              <div className="space-y-2"><Label>Minimum years of experience</Label>
                <Input type="number" min={0} step={0.5} value={req.min_years_experience ?? ""} data-testid="review-min-years-input"
                  onChange={(e) => setReq("min_years_experience", e.target.value === "" ? null : Number(e.target.value))} /></div>
            </div>
            <LegalFields form={job} setForm={setJob} prefix="review" />
          </Section>
          <Section title="Skills" tip="Must-have skills weigh heavily in the Fit Score. Nice-to-have skills add a bonus." testId="review-skills">
            <Label className="text-xs text-ink-muted">Must-have</Label>
            <ChipInput values={req.must_have_skills} onChange={(v) => setReq("must_have_skills", v)} testId="must-have-skills" />
            <Label className="text-xs text-ink-muted">Nice-to-have</Label>
            <ChipInput values={req.nice_to_have_skills} onChange={(v) => setReq("nice_to_have_skills", v)} testId="nice-to-have-skills" tone="teal" />
          </Section>
          <Section title="Background" testId="review-background">
            <Label className="text-xs text-ink-muted">Industries</Label>
            <ChipInput values={req.industries} onChange={(v) => setReq("industries", v)} testId="industries" tone="teal" />
            <Label className="text-xs text-ink-muted">Education</Label>
            <ChipInput values={req.education} onChange={(v) => setReq("education", v)} testId="education" tone="teal" />
            <Label className="text-xs text-ink-muted">Certifications</Label>
            <ChipInput values={req.certifications} onChange={(v) => setReq("certifications", v)} testId="certifications" tone="teal" />
          </Section>
          <Section title="Responsibilities" testId="review-responsibilities">
            <ListEditor items={req.responsibilities} onChange={(v) => setReq("responsibilities", v)} testId="responsibilities" placeholder="Add responsibility" />
          </Section>
          <Section title="Knockout questions" tip="Yes/no questions asked when someone applies. A wrong answer flags the candidate for review; it never auto-rejects without a person checking." testId="review-knockout">
            <KnockoutEditor questions={req.knockout_questions} onChange={(v) => setReq("knockout_questions", v)} />
          </Section>
          <Section title="Ideal profile" testId="review-ideal-profile">
            <TraitsEditor traits={job.ideal_profile?.traits} onChange={(traits) => set("ideal_profile", { traits })} />
          </Section>
          <Section title="Interview focus areas" tip="Topics the AI interview will probe with follow-up questions." testId="review-focus-areas">
            <ChipInput values={job.interview_config?.focus_areas} onChange={(v) => set("interview_config", { ...job.interview_config, focus_areas: v })} testId="focus-areas" />
          </Section>
        </div>
      </div>
      <div className="sticky bottom-4 z-20 flex items-center justify-between max-sm:flex-wrap max-sm:gap-2 gap-4 rounded-[6px] border border-line bg-white px-5 py-3 shadow-[0_8px_24px_rgba(27,30,26,0.10)]">
        <p className="text-sm text-ink-secondary">Check the interpretation, edit anything that's off, then confirm.</p>
        <Button onClick={onConfirm} disabled={confirming} data-testid="confirm-requirements-btn">
          {confirming ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Confirm requirements
        </Button>
      </div>
    </div>
  );
}
